import { apiRequest } from '@/lib/api';
import type { ApprovalDeliveryMethod } from "@/lib/tickets";

export type ScheduleBlockStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'PAUSED' | 'DONE' | 'CANCELED' | 'MISSED';
export type ScheduleBlockSource = 'MANUAL' | 'APPROVAL_TRIGGER';

export type ScheduleBlockItem = {
  id: string;
  companyId: string;
  projectId: string;
  createdById: string;
  startAt: string;
  endAt: string;
  durationMinutes?: number | null;
  notes?: string | null;
  status: ScheduleBlockStatus;
  source: ScheduleBlockSource;
  assignedMemberId?: string | null;
  createdAt: string;
  updatedAt: string;
  project: {
    id: string;
    name: string;
    code?: string | null;
    jobSiteAddress?: string | null;
    assignedMember?: {
      id: string;
      fullName: string;
      email: string;
      role?: string;
    } | null;
    client?: {
      id: string;
      name: string;
      companyName?: string | null;
    } | null;
  };
  createdBy: {
    id: string;
    fullName: string;
    email: string;
  };
  assignedMember?: {
    id: string;
    fullName: string;
    email: string;
    role?: string;
  } | null;
};

export type ScheduleBlocksResponse = {
  date: string | null;
  items: ScheduleBlockItem[];
};

export function fetchScheduleBlocks(input: { date?: string; projectId?: string; futureOnly?: boolean; assignedMemberId?: string }) {
  const query = new URLSearchParams();
  if (input.date) query.set('date', input.date);
  if (input.projectId) query.set('projectId', input.projectId);
  if (input.futureOnly) query.set('futureOnly', 'true');
  if (input.assignedMemberId) query.set('assignedMemberId', input.assignedMemberId);

  return apiRequest<ScheduleBlocksResponse>(`/schedule/blocks?${query.toString()}`, {
    method: 'GET',
  });
}

export function createScheduleBlock(payload: {
  projectId: string;
  startAt: string;
  endAt?: string;
  durationMinutes?: number;
  notes?: string;
  assignedMemberId?: string;
  source?: ScheduleBlockSource;
}) {
  return apiRequest<ScheduleBlockItem>('/schedule/blocks', {
    method: 'POST',
    body: payload,
  });
}

export function updateScheduleBlock(
  id: string,
  payload: {
    startAt?: string;
    endAt?: string;
    durationMinutes?: number;
    notes?: string;
    status?: ScheduleBlockStatus;
    source?: ScheduleBlockSource;
    assignedMemberId?: string;
  },
) {
  return apiRequest<ScheduleBlockItem>(`/schedule/blocks/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteScheduleBlock(id: string) {
  return apiRequest<{ success: true }>(`/schedule/blocks/${id}`, {
    method: 'DELETE',
  });
}

export function sendProjectScheduleMessage(payload: {
  projectId: string;
  blockIds: string[];
  deliveryMethod?: ApprovalDeliveryMethod;
  smsConsentConfirmed?: boolean;
}) {
  return apiRequest<{
    success: true;
    deliveryMethod: ApprovalDeliveryMethod;
    recipientEmail: string | null;
    recipientPhone: string | null;
    sentChannels: Array<"email" | "sms">;
    failedChannels: Array<{ channel: "email" | "sms"; message: string }>;
    blockIds: string[];
  }>('/schedule/blocks/send-project-message', {
    method: 'POST',
    body: payload,
  });
}
